import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, createTheme, Tab, ThemeProvider } from "@mui/material";
import { CirclePlus } from "lucide-react";
import React, { useState, useEffect } from "react";

const RecieptDetails = ({
  recieptSection,
  totals,
  data,
  onChange,
  paymentModes,
  BASE_URL,
  setCommissionAccounts,
  DEPARTMENT,
}) => {
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";
  const [parentTabValue, setParentTabValue] = useState("1");
  const [visiblePaymentCount, setVisiblePaymentCount] = useState(1);

  //  first payment mode on component mount or when paymentModes change
  useEffect(() => {
    if (paymentModes.length > 0 && !data.rcvd_mode1) {
      const firstMode = paymentModes[0];
      onChange({
        ...data,
        rcvd_mode1: firstMode.Rcvd_Mode,
        rcvd_mode1_acc: firstMode.Account,
      });
    }
  }, [paymentModes, data.rcvd_mode1, onChange]);

  useEffect(() => {
    let total = 0;
    for (let i = 1; i <= visiblePaymentCount; i++) {
      const amt = parseFloat(data[`rcvd_mode${i}_amt`] || 0);
      total += isNaN(amt) ? 0 : amt;
    }

    if (data.totalAmount !== total) {
      onChange({ ...data, totalAmount: total });
    }
  }, [data, visiblePaymentCount, onChange]);

  const handleAddPaymentMode = () => {
    if (visiblePaymentCount < paymentModes.length) {
      const newCount = visiblePaymentCount + 1;
      setVisiblePaymentCount(newCount);

      // Auto-select the first available mode for the new payment section
      const selectedModes = [];
      for (let i = 1; i <= visiblePaymentCount; i++) {
        if (data[`rcvd_mode${i}`]) {
          selectedModes.push(data[`rcvd_mode${i}`]);
        }
      }

      const availableMode = paymentModes.find(
        (mode) => !selectedModes.includes(mode.Rcvd_Mode),
      );

      if (availableMode) {
        onChange({
          ...data,
          [`rcvd_mode${newCount}`]: availableMode.Rcvd_Mode,
          [`rcvd_mode${newCount}_acc`]: availableMode.Account,
        });
      }
    }
  };

  const handleRemovePaymentMode = () => {
    if (visiblePaymentCount > 1) {
      setVisiblePaymentCount(visiblePaymentCount - 1);
      // Clear removed payment mode data
      const idx = visiblePaymentCount;
      onChange({
        ...data,
        [`rcvd_mode${idx}`]: "",
        [`rcvd_mode${idx}_acc`]: "",
        [`rcvd_mode${idx}_amt`]: 0.0,
      });
    }
  };

  const handleParentTabChange = (event, newValue) => {
    setParentTabValue(newValue);
  };

  const updateData = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const theme = createTheme({
    typography: {
      fontFamily: `'Figtree', sans-serif`,
    },
  });
  useEffect(() => {
    const fetchCommissionAccounts = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/Scrn_code/SI/-/${DEPARTMENT}/-`,
        );
        const data = await res.json();

        const expenseAcc =
          data?.find((acc) => acc.Scrn_code === "SI-COMMI")?.ACCOUNT || "-";
        const payableAcc =
          data.find((acc) => acc.Scrn_code === "SI-COMMI-C")?.ACCOUNT || "-";

        setCommissionAccounts({
          expense: expenseAcc,
          payable: payableAcc,
        });
      } catch (err) {
        console.error("Error fetching commission accounts:", err);
      }
    };

    fetchCommissionAccounts();
  }, []);

  return (
    <div>
      {recieptSection ? (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 mt-2">
          <div className="p-4">
            <ThemeProvider theme={theme}>
              <TabContext value={parentTabValue}>
                <Box
                  sx={{
                    padding: 0,
                    margin: 0,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <TabList onChange={handleParentTabChange} aria-label="">
                    <Tab label="Receipt" value="1" />
                    <Tab label="Commission" value="2" />
                  </TabList>
                </Box>

                <TabPanel value="1" sx={{ p: 0, marginTop: 2 }}>
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-800">
                          Mode of Receipt
                        </h4>
                        {visiblePaymentCount < paymentModes.length && (
                          <button
                            onClick={handleAddPaymentMode}
                            title="Add new mode of Receipt"
                            className="flex items-center gap-1 px-2 py-1.5 font-medium text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                          >
                            <CirclePlus size={16} /> Add
                          </button>
                        )}
                      </div>

                      {[...Array(visiblePaymentCount)].map((_, idx) => {
                        const selectedMode = data[`rcvd_mode${idx + 1}`] || "";

                        // Get all selected modes except for this current dropdown
                        const selectedModes = [];
                        for (let i = 1; i <= visiblePaymentCount; i++) {
                          if (i !== idx + 1 && data[`rcvd_mode${i}`]) {
                            selectedModes.push(data[`rcvd_mode${i}`]);
                          }
                        }

                        // Filter available modes
                        const availableModes = paymentModes.filter(
                          (mode) => !selectedModes.includes(mode.Rcvd_Mode),
                        );

                        const selectedModeObj = paymentModes.find(
                          (m) => m.Rcvd_Mode === selectedMode,
                        );

                        return (
                          <div
                            key={idx}
                            className="bg-gray-50 p-3 rounded-xl border border-l-4 border-l-blue-500 border-slate-300"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-600">
                                Mode {idx + 1}
                              </span>
                              {idx === visiblePaymentCount - 1 &&
                                visiblePaymentCount > 1 && (
                                  <button
                                    onClick={handleRemovePaymentMode}
                                    className="text-red-500 hover:text-red-700 text-xs"
                                  >
                                    Remove
                                  </button>
                                )}
                            </div>

                            <div className="grid grid-cols-3 md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Type
                                </label>
                                <select
                                  value={selectedMode}
                                  onChange={(e) => {
                                    const modeObj = paymentModes.find(
                                      (m) => m.Rcvd_Mode === e.target.value,
                                    );
                                    onChange({
                                      ...data,
                                      [`rcvd_mode${idx + 1}`]: e.target.value,
                                      [`rcvd_mode${idx + 1}_acc`]:
                                        modeObj?.Account || "",
                                    });
                                  }}
                                  className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  {availableModes.map((mode) => (
                                    <option
                                      key={mode.Rcvd_Mode}
                                      value={mode.Rcvd_Mode}
                                    >
                                      {mode.Rcvd_Mode}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  A/C No
                                </label>
                                <div className="flex items-center h-9">
                                  {data[`rcvd_mode${idx + 1}_acc`] ||
                                  selectedModeObj?.Account ? (
                                    <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 shadow-sm">
                                      <span className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse"></span>
                                      {data[`rcvd_mode${idx + 1}_acc`] ||
                                        selectedModeObj?.Account}
                                    </div>
                                  ) : (
                                    <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                                      <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
                                      No Account
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Amount
                                </label>
                                <input
                                  type="number"
                                  value={data[`rcvd_mode${idx + 1}_amt`] || ""}
                                  onChange={(e) =>
                                    onChange({
                                      ...data,
                                      [`rcvd_mode${idx + 1}_amt`]:
                                        parseFloat(e.target.value) || 0,
                                    })
                                  }
                                  placeholder="0.00"
                                  step="0.01"
                                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </TabPanel>

                <TabPanel value="2" sx={{ p: 0, marginTop: 2 }}>
                  <div>
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label
                            title="Commission Expense"
                            className="block text-xs font-medium text-gray-700"
                          >
                            COMM. EXPENSE
                          </label>
                          <p className=" text-orange-600 text-xs font-semibold">
                            #######
                          </p>
                        </div>
                        <input
                          type="text"
                          name="commissionExpense"
                          value={data.commissionExpense}
                          onChange={(e) =>
                            updateData("commissionExpense", e.target.value)
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label
                            title="Commission payable A/C"
                            className="block text-xs font-medium text-gray-700"
                          >
                            COMM. PAY A/C
                          </label>{" "}
                          <p className=" text-orange-600 text-xs font-semibold">
                            #######
                          </p>
                        </div>
                        <input
                          type="text"
                          name="commissionPayable"
                          value={data.commissionPayable}
                          onChange={(e) =>
                            updateData("commissionPayable", e.target.value)
                          }
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 items-center gap-2 mt-3">
                      <div className="">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={data.itemWise || false}
                            onChange={(e) =>
                              updateData("itemWise", e.target.checked)
                            }
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span
                            title="Item Wise"
                            className="ml-2 text-sm font-medium text-gray-700"
                          >
                            ITEM WISE
                          </span>
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          COMM. AMOUNT
                        </label>
                        <input
                          type="text"
                          name="commissionAmount"
                          value={data.commissionAmount || ""}
                          onChange={(e) =>
                            updateData("commissionAmount", e.target.value)
                          }
                          className="w-full px-3 py-1.5 text-normal font-medium border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </TabContext>
            </ThemeProvider>

            <div className="flex flex-col space-y-3 mt-4">
              <div className="flex items-center gap-2">
                <div>
                  <label
                    title="Advance RV number"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    ADV. RV NO
                  </label>
                  <input
                    type="text"
                    name="advanceRvNo"
                    value={data.advanceRvNo}
                    onChange={(e) => updateData("advanceRvNo", e.target.value)}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ADV. RV AMT
                  </label>
                  <input
                    type="text"
                    name="advanceRvAmount"
                    value={data.advanceRvAmount}
                    onChange={(e) =>
                      updateData("advanceRvAmount", e.target.value)
                    }
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-sm text-end font-medium text-gray-700 mb-1">
                  Total Amount Received
                </label>
                <input
                  type="text"
                  name="total"
                  value={data.totalAmount}
                  readOnly
                  className="w-full px-3 py-2 text-lg font-bold border border-gray-300 rounded-md text-right bg-green-50 focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RecieptDetails;
